/**
 * DataSourceManager.js
 * Manages API-backed data sources for form fields (dropdowns, autocomplete).
 * Fetches options from API endpoints, caches responses, supports dependent/cascading sources.
 *
 * Usage:
 *   const dsm = new DataSourceManager(formConfig.dataSources, sId, uId);
 *   await dsm.loadInitialSources();
 *   const options = dsm.getOptions("ds_companies");
 *   dsm.subscribe("ds_companies", (options) => setState({ ... }));
 */

import configData from "../../config.json";

class DataSourceManager {
  /**
   * @param {Array} dataSources - Array of dataSource definitions from formConfig
   * @param {string} sId - Session ID for auth
   * @param {string} uId - User ID for auth
   */
  constructor(dataSources, sId, uId) {
    this.dataSources = dataSources || [];
    this.sId = sId || sessionStorage.getItem("sessionId");
    this.uId = uId || sessionStorage.getItem("uId");

    // Cache: { cacheKey: { options: [...], timestamp: Date.now() } }
    this.cache = {};

    // Subscribers: { dsId: [callback, ...] }
    this.listeners = {};

    // Loading state: { dsId: boolean }
    this.loading = {};
  }

  /**
   * Get a data source definition by ID
   */
  getDefinition(dsId) {
    return this.dataSources.find((ds) => ds.id === dsId) || null;
  }

  /**
   * Fetch options for a data source, optionally with filter params (for cascading)
   * @param {string} dsId - Data source ID
   * @param {Object} params - Additional parameters to send (e.g. { company_id: 5 })
   * @returns {Promise<Array>} - Array of { value, label } options
   */
  async fetchOptions(dsId, params = {}) {
    const ds = this.getDefinition(dsId);
    if (!ds) {
      console.warn(`DataSourceManager: Unknown data source "${dsId}"`);
      return [];
    }

    // Check cache
    const cacheKey = this._buildCacheKey(dsId, params);
    if (ds.cache !== false && this.cache[cacheKey]) {
      return this.cache[cacheKey].options;
    }

    // Mark as loading
    this.loading[dsId] = true;

    try {
      const formData = new FormData();
      formData.append("sId", this.sId);
      formData.append("uId", this.uId);

      // Append filter params
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      });

      const response = await fetch(configData.api_url + ds.endpoint, {
        method: "POST",
        mode: "cors",
        body: formData,
      });

      const data = await response.json();

      // Extract list from various response formats
      const list = data.list || data.data || data.options || [];

      // Map to { value, label } using configured field names
      const labelField = ds.labelField || "name";
      const valueField = ds.valueField || "id";

      const options = Array.isArray(list)
        ? list.map((item) => ({
            value: String(item[valueField] ?? ""),
            label: String(item[labelField] ?? item[valueField] ?? ""),
            _raw: item, // Preserve raw data for auto-fill scenarios
          }))
        : [];

      // Cache the result
      if (ds.cache !== false) {
        this.cache[cacheKey] = { options, timestamp: Date.now() };
      }

      // Notify subscribers
      this._notifyListeners(dsId, options);

      return options;
    } catch (error) {
      console.error(`DataSourceManager: Error fetching "${dsId}":`, error);
      return [];
    } finally {
      this.loading[dsId] = false;
    }
  }

  /**
   * Get cached options for a data source (synchronous, returns [] if not yet loaded)
   * @param {string} dsId - Data source ID
   * @param {Object} params - Filter params for cache key matching
   * @returns {Array} - Cached options or empty array
   */
  getOptions(dsId, params = {}) {
    const cacheKey = this._buildCacheKey(dsId, params);
    return this.cache[cacheKey] ? this.cache[cacheKey].options : [];
  }

  /**
   * Check if a data source is currently loading
   */
  isLoading(dsId) {
    return this.loading[dsId] || false;
  }

  /**
   * Refresh a data source (clear cache and re-fetch)
   * @param {string} dsId - Data source ID
   * @param {Object} params - Filter params
   * @returns {Promise<Array>}
   */
  async refreshDataSource(dsId, params = {}) {
    // Clear existing cache for this source
    const cacheKey = this._buildCacheKey(dsId, params);
    delete this.cache[cacheKey];
    return this.fetchOptions(dsId, params);
  }

  /**
   * Fetch a dependent data source with parent value.
   * Used for cascade interactions (e.g. company changes -> reload employees).
   * @param {string} dsId - Data source ID
   * @param {string} parentValue - The parent field's current value
   * @returns {Promise<Array>}
   */
  async fetchDependentOptions(dsId, parentValue) {
    const ds = this.getDefinition(dsId);
    if (!ds || !ds.dependsOn) return this.fetchOptions(dsId);

    const paramField = ds.dependsOnParam || ds.dependsOn;
    return this.fetchOptions(dsId, { [paramField]: parentValue });
  }

  /**
   * Load all non-dependent data sources on mount.
   * Returns a Promise that resolves when all initial sources are loaded.
   */
  async loadInitialSources() {
    const independent = this.dataSources.filter((ds) => !ds.dependsOn);
    const results = await Promise.allSettled(
      independent.map((ds) => this.fetchOptions(ds.id))
    );

    // Build a map of dsId -> options for initial state
    const optionsMap = {};
    independent.forEach((ds, i) => {
      if (results[i].status === "fulfilled") {
        optionsMap[ds.id] = results[i].value;
      } else {
        optionsMap[ds.id] = [];
      }
    });

    return optionsMap;
  }

  /**
   * Subscribe to option changes for a data source.
   * Called whenever fetchOptions completes for that source.
   * @param {string} dsId - Data source ID
   * @param {Function} callback - Called with (options: Array)
   * @returns {Function} - Unsubscribe function
   */
  subscribe(dsId, callback) {
    if (!this.listeners[dsId]) {
      this.listeners[dsId] = [];
    }
    this.listeners[dsId].push(callback);

    // Return unsubscribe function
    return () => {
      this.listeners[dsId] = (this.listeners[dsId] || []).filter(
        (cb) => cb !== callback
      );
    };
  }

  /**
   * Clear all caches and listeners (call on unmount)
   */
  destroy() {
    this.cache = {};
    this.listeners = {};
    this.loading = {};
  }

  // ─── Private Helpers ────────────────────────────────────────────────────

  _buildCacheKey(dsId, params = {}) {
    const paramStr = Object.keys(params).length > 0
      ? "_" + Object.entries(params).sort().map(([k, v]) => `${k}=${v}`).join("&")
      : "";
    return dsId + paramStr;
  }

  _notifyListeners(dsId, options) {
    (this.listeners[dsId] || []).forEach((cb) => {
      try {
        cb(options);
      } catch (e) {
        console.error("DataSourceManager listener error:", e);
      }
    });
  }
}

export default DataSourceManager;
